// TP Route Storage Service - Memory-based storage like VRP
import { useState, useCallback, useRef } from 'react';

// Hook for memory-based route storage
export const useTpRouteStorage = () => {
  // Route storage for different modes - pure memory
  const [autoRoutes, setAutoRoutes] = useState(null);
  const [customRoutes, setCustomRoutes] = useState([]);
  const [routeLocations, setRouteLocations] = useState([]);
  
  // Used to prevent circular updates
  const prevAutoRoutesRef = useRef(null);
  const prevCustomRoutesRef = useRef(null);
  
  // Save auto routes (from Auto Path mode)
  const saveAutoRoutes = useCallback((routeData) => {
    prevAutoRoutesRef.current = routeData ? { ...routeData } : null;
    setAutoRoutes(routeData);
  }, []);

  // Save custom routes (from Custom Path mode)
  const saveCustomRoutes = useCallback((routes, locations) => {
    const routeData = {
      routes: routes || [],
      locations: locations || [],
      timestamp: new Date().toISOString()
    };
    
    prevCustomRoutesRef.current = { ...routeData };
    setCustomRoutes(routeData.routes);
    setRouteLocations(routeData.locations);
  }, []);

  // Get stored routes for a specific mode
  const getStoredRoutes = useCallback((mode) => {
    if (mode === 'Auto Path') {
      return autoRoutes;
    } else if (mode === 'Custom Path') {
      return {
        routes: customRoutes,
        locations: routeLocations
      };
    }
    return null;
  }, [autoRoutes, customRoutes, routeLocations]);

  // Clear all stored routes
  const clearAllRoutes = useCallback(() => {
    setAutoRoutes(null);
    setCustomRoutes([]);
    setRouteLocations([]);
    prevAutoRoutesRef.current = null;
    prevCustomRoutesRef.current = null;
  }, []);

  // Clear specific mode routes
  const clearAutoRoutes = useCallback(() => {
    setAutoRoutes(null);
    prevAutoRoutesRef.current = null;
  }, []);

  const clearCustomRoutes = useCallback(() => {
    setCustomRoutes([]);
    setRouteLocations([]);
    prevCustomRoutesRef.current = null;
  }, []);

  return {
    // Storage state
    autoRoutes,
    customRoutes,
    routeLocations,
    
    // Storage operations
    saveAutoRoutes,
    saveCustomRoutes,
    getStoredRoutes,
    clearAllRoutes,
    clearAutoRoutes,
    clearCustomRoutes,
    
    // References
    prevAutoRoutesRef,
    prevCustomRoutesRef
  };
};
